import { useState, FormEvent } from 'react';

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('mobile_alt') as string || '').trim();
    if (honeypotValue) {
      setStatus('success');
      form.reset();
      return;
    }

    formData.delete('mobile_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qtqncnlsngrm4lm5s0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
        setStatus('error');
        setErrorMsg(serverMsg || '提交失败，请稍后再试');
      } else {
        setStatus('error');
        setErrorMsg(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8"
      noValidate
    >
      {status === 'success' && (
        <div className="mb-6 p-4 bg-accent-100 text-accent-800 rounded-lg text-sm">
          感谢您的留言！我们会在24小时内通过邮件回复您。
        </div>
      )}

      {status === 'error' && errorMsg && (
        <div className="mb-6 p-4 bg-primary-100 text-primary-800 rounded-lg text-sm">
          {errorMsg}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
            您的姓名 <span className="text-primary-500">*</span>
          </label>
          <input
            type="text"
            id="contact-name"
            name="name"
            required
            className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
            placeholder="请输入您的姓名"
          />
        </div>

        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
            电子邮箱 <span className="text-primary-500">*</span>
          </label>
          <input
            type="email"
            id="contact-email"
            name="email"
            required
            className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
            placeholder="请输入您的邮箱"
          />
        </div>
      </div>

      <div className="mb-4">
        <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
          主题
        </label>
        <input
          type="text"
          id="contact-subject"
          name="subject"
          className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
          placeholder="请简要描述您的问题"
        />
      </div>

      <div className="mb-6">
        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
          留言内容 <span className="text-primary-500">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
          placeholder="请输入您想咨询的内容（最多500字）"
        ></textarea>
        <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
      </div>

      <input
        type="text"
        name="mobile_alt"
        className="honeypot-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <button
        type="submit"
        disabled={status === 'loading'}
        className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer whitespace-nowrap"
      >
        {status === 'loading' ? (
          <span className="flex items-center justify-center gap-2">
            <div className="w-4 h-4 border-2 border-background-50/30 border-t-background-50 rounded-full animate-spin"></div>
            提交中...
          </span>
        ) : (
          '发送留言'
        )}
      </button>
    </form>
  );
}